import { getCurrencySymbol } from './currency';
/**
 * Single, centralized salary formatter for a job position.
 *
 * Takes the raw salary fields exactly as they come from the backend (salary /
 * min_salary / max_salary / salary_type / salary_currency / salary_month_number
 * / payment_type_var) and returns the one display string used across every
 * surface: job detail page, map popup, explorer cards. Keeping a single
 * function avoids the previous drift between the detail formatter and the card
 * preview formatter.
 *
 * Output shape: amount + currency symbol + net/gross ("netti"/"lordi") + period
 * ("mensili"/"all'ora"/"a servizio"). Annual gross figures (RAL) are shown in
 * compact "k" notation with a "RAL" label ("€56.5k RAL", "€24k - 27k RAL") so
 * they can't be misread as monthly pay. Returns null when the position carries
 * no salary, so callers can decide their own fallback (e.g. "Stipendio da
 * concordare").
 *
 * ──────────────────────────────────────────────────────────────────────────
 * ⚠️  CROSS-REPO SYNC — this function has a TWIN in the other repository:
 *       • restworld-frontend (here): libs/utils-common/src/utils/salary.ts → formatJobPositionSalary
 *       • website:                   src/lib/cerco-lavoro/salary.ts        → formatSalary
 *     Same algorithm; only the field casing differs (the website maps backend
 *     fields to camelCase). User-facing words are injected via a `SalaryLabels`
 *     object built from i18n (never hardcoded), so keep the label set in sync in
 *     both repos too. If you change ANY salary-display rule here (k-notation,
 *     RAL / lordi / netti labels, payment period, currency), make the IDENTICAL
 *     change in the other repo's twin in the same change-set — otherwise the
 *     live worker app and the SEO pages drift apart.
 *
 *     ONE intentional difference (see the "payment period" block below): here
 *     the monthly period is suppressed when `salary_month_number` is set,
 *     because the worker-app card shows the instalment count in a separate
 *     "per N mensilità" label. The website twin has no such label: it treats
 *     monthly as the implicit default (no "mensili") and only appends a
 *     non-standard instalment count ("· 13 mens.", omitted for the standard 12).
 * ──────────────────────────────────────────────────────────────────────────
 */

/** Compact "k" notation: 24000 → "24k", 56500 → "56.5k". */

var toK = function toK(value) {
  var thousands = Math.round(value / 1000 * 10) / 10;
  return "".concat(Number.isInteger(thousands) ? thousands.toFixed(0) : thousands.toFixed(1), "k");
};
/**
 * Italian number with the thousands separator forced ("1.700", "56.500").
 * Manual grouping (dot every 3 digits) instead of `Intl.NumberFormat` with
 * `useGrouping: 'always'`: the default it-IT CLDR rule omits the separator for
 * 4-digit numbers ("1700"), and this repo's TS lib doesn't type the 'always'
 * option. Output matches the website twin's `formatNumberIt`.
 */


var formatThousands = function formatThousands(value) {
  return Math.round(value).toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');
};
/**
 * Localized words used to build the salary string. Injected by the caller from
 * the i18n catalogue, because this is a pure function (not a React component)
 * and must not hardcode user-facing copy.
 */


/**
 * Builds the `SalaryLabels` from a translation function (`t`) scoped to the
 * `common` namespace. Keep the key names in sync with the locale catalogues
 * under each app's `public/locales/{it,en}/common.json`.
 */
export var salaryLabelsFromT = function salaryLabelsFromT(t) {
  return {
    net: t('salary_net'),
    gross: t('salary_gross'),
    ral: t('salary_ral'),
    monthly: t('salary_monthly'),
    hourly: t('salary_hourly'),
    perService: t('salary_per_service'),
    from: t('salary_from'),
    upTo: t('salary_up_to')
  };
};
export var formatJobPositionSalary = function formatJobPositionSalary(jobPosition, labels) {
  var _ref = jobPosition || {},
      salary = _ref.salary,
      min_salary = _ref.min_salary,
      max_salary = _ref.max_salary,
      salary_type = _ref.salary_type,
      salary_currency = _ref.salary_currency,
      salary_month_number = _ref.salary_month_number,
      payment_type_var = _ref.payment_type_var;

  if (salary == null && min_salary == null && max_salary == null) return null;
  var currency = getCurrencySymbol(salary_currency); // "k" notation only for annual gross (RAL); monthly/hourly stay as full
  // numbers with the Italian thousands separator ("1.700").

  var isAnnual = (payment_type_var === null || payment_type_var === void 0 ? void 0 : payment_type_var.key) === 'annual' && salary_type === 'gross';

  var renderAmount = function renderAmount(value) {
    return isAnnual ? toK(value) : formatThousands(value);
  };

  var salaryString = '';
  var isMinSalaryOnlyPresent = min_salary != null && max_salary == null;
  var isMaxSalaryOnlyPresent = max_salary != null && min_salary == null; // Currency always before the amount: "€1.700", "Partirà da €1.700", "€56.5k".

  if (isMinSalaryOnlyPresent) salaryString = "".concat(labels.from, " ").concat(currency).concat(renderAmount(min_salary));else if (isMaxSalaryOnlyPresent) salaryString = "".concat(labels.upTo, " ").concat(currency).concat(renderAmount(max_salary));else {
    var salaryAmt = min_salary != null && max_salary != null ? "".concat(renderAmount(min_salary), " - ").concat(renderAmount(max_salary)) : salary != null ? renderAmount(salary) : '';
    salaryString = "".concat(currency).concat(salaryAmt);
  } // Net / gross, or "RAL" for an annual gross figure (Retribuzione Annua Lorda).

  var salaryLabel = isAnnual ? labels.ral : salary_type === 'gross' ? labels.gross : labels.net;
  salaryString = salaryString.concat(" ".concat(salaryLabel)); // Payment period. Skipped for RAL ("annual" is already implied by the label),
  // and only when there is no explicit number of monthly instalments. The
  // 'annual' payment type never reaches here (it's handled as RAL above), so the
  // switch only covers hourly / per-service / monthly.

  if (!isAnnual && payment_type_var && !salary_month_number) {
    var paymentTypeLabel = '';

    switch (payment_type_var.key) {
      case 'hour':
        paymentTypeLabel = labels.hourly;
        break;

      case 'service':
        paymentTypeLabel = labels.perService;
        break;

      default:
        paymentTypeLabel = labels.monthly;
    }

    salaryString = salaryString.concat(" ".concat(paymentTypeLabel));
  }

  return salaryString;
};